// NodeLabels.js

// Node Types Cache
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';

// Utilities
import { buildSearchIndexFromTexts } from 'services/search/SearchIndexService';

// Icons
import React from 'react';
import Icon from 'svg/Icon';

export const NodeOptionNames = {
	CONTENT: 'Content',
	DESCRIPTION: 'Description',
	LOCATION: 'Location',
	SETTINGS: 'Settings',
	TITLE: 'Title',
	TYPE: 'Type',
};

/**
 * Generates a label for a node option based upon:
 * - Node type
 * - Option type (title, description, settings, location)
 * 
 * @param {string} type node type
 * @param {string} option NodeOptionNames item
 * @param {string} language language code (defaults to 'en')
 * 
 * @returns {String} e.g. "Photo Title", "Event Location"
 * 
 * @example getNodeOptionLabel('PHOTO', NodeOptionNames.TITLE) // "Photo Title"
 */
export const getNodeOptionLabel = async (type, option, language = 'en') => {
	const nodeType = getNodeType(type);
	const typeLabel = nodeType?.label || '';
	return `${typeLabel} ${NodeOptionNames[option]}`
};

/**
 * Generates a summary phrase for node settings based on a prefix.
 * 
 * @param {object} node - The node object with flattened settings.
 * @param {string} prefix - The settings prefix ("privacy", "contribution", "comments").
 * 
 * @returns {string} A summary phrase of the settings.
 */
export function getOptionsSummaryPhrase(node, prefix) {
	// Handle "Only you" case first
	if (node[`${prefix}OnlyYou`]) {
		return "Only you";
	}

	// Handle everyone cases
	if (node[`${prefix}Everyone`]) {
		if (node[`${prefix}Everywhere`] && node[`${prefix}SelectedLocations`].length === 0) {
			return "Everyone, everywhere";
		}

		if (node[`${prefix}SelectedLocations`].length > 0) {
			const locationTitles = node[`${prefix}SelectedLocations`].map(loc => {
				// Check if location has address details
				if (loc.streetNumber && loc.route) {
					const addressParts = [loc.streetNumber, loc.route];
					if (loc.subpremise) {
						addressParts.push(loc.subpremise);
					}
					return `${addressParts.join(' ')} in ${loc.title}`;
				}
				return loc.title;
			});

			if (locationTitles.length === 1) {
				return `Everyone in ${locationTitles[0]}`;
			}
			if (locationTitles.length === 2) {
				return `Everyone in ${locationTitles[0]} and ${locationTitles[1]}`;
			}
			if (locationTitles.length === 3) {
				return `Everyone in ${locationTitles[0]}, ${locationTitles[1]} and ${locationTitles[2]}`;
			}
			return `Everyone in ${locationTitles[0]}, ${locationTitles[1]} and ${locationTitles.length - 2} other locations`;
		}

		return "Everyone, everywhere"; // Default everyone case
	}

	// Fallback case
	return `${prefix.charAt(0).toUpperCase() + prefix.slice(1)} settings not specified`;
}

/**
* Generates a phrase based on node visibility settings.
 * 
 * @param {object} node - The node object with privacy fields.
 * 
 * @returns {ReactNode} - The phrase.
*/
export const getParticipantsPhrase = (node) => {

	// Default fallback
	return "Everyone";
};

/**
* Determines which privacy icon to display based on node visibility settings.
 * 
 * @param {object} node - The node object with privacy fields.
 * 
 * @returns {ReactNode} - The icon component to render.
*/
export const getPrivacyIcon = (node) => {
	// All other cases imply some level of restriction
	const PrivacyIcon = (props) => (
		React.createElement(Icon, { iconKey: 'visibility', ...props })
	);
	PrivacyIcon.displayName = 'PrivacyIcon';
	return PrivacyIcon;
};

/**
 * Returns the label for the "Add Children" modal based on the active node's title or type.
 */
export const getAddChildrenLabel = (node) => {
	if (!node) return "Add";

	if (node.title) {
		let title = node.title;

		// If title is longer than 30 characters, trim safely at a word boundary
		if (title.length > 30) {
			// Take the first 30 characters
			let trimmed = title.slice(0, 30);

			// Find the last space within that trimmed chunk
			const lastSpace = trimmed.lastIndexOf(' ');

			if (lastSpace > 0) {
				trimmed = trimmed.slice(0, lastSpace); // cut cleanly at the last space
			}

			title = trimmed + '...';
		}

		return title;
	} else {
		return "Add Content";
	}
};

// ------ SEARCH INDEXING ------ //

/**
 * buildNodeSearchIndex
 * 
 * Builds a search index array from the provided title and tags. Title substrings 
 * and whole tags are included in the index.
 * 
 * @param {string} title - The title of the node.
 * @param {array} tags - An array of tags associated with the node.
 * 
 * @returns {array} - An array of unique search index terms.
 */
export const buildNodeSearchIndex = ({ title = '', tags = [] } = {}) => {

    // Sanitize inputs
    const safeTitle = typeof title === 'string' ? title : '';

    // Ensure tags is an array of strings
    const safeTags = Array.isArray(tags) ? tags : [];

	return buildSearchIndexFromTexts([safeTitle, safeTags]);
};

/**
 * Get the localized label for a field based on the current language.
 * Falls back to field.label if translations are not available.
 *
 * @param {object} field - The field object
 * @param {string} language - The current language code (e.g., 'en', 'fr')
 * @returns {string} The localized label
 */
export function getFieldLabel(field, language = 'en') {
  if (!field) return '';
	return field.label || '';
}
